import React, { useState, useMemo, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import type { ExportedResourceDetail, Migration } from '../../../types';
import { getStatusLabel } from '../../../utils/migrationStatus';
import { SnapshotHistoryTable } from '../Snapshot/SnapshotHistoryTable';
import { useToast } from '../../Toast/ToastProvider';
import { ui } from '../../../config/uiSizes';
import { Tooltip } from '../../Tooltip';
import { useAuth } from '../../../hooks/auth';
import { useSnapshotGuidanceSeen } from '../../../hooks/ui/useSnapshotGuidanceSeen';
import { downloadHistoricalSnapshot } from '../../../services/Snapshot/snapshotHistoryService';
import {
  downloadMigrationResults,
  NoResultsAvailableError
} from '../../../services/Migration/downloadMigrationResultsService';
import { useFetchExportedResources } from '../../../hooks/api';
import { getTypeLabelByApiName } from '../../../utils/resourceTypes';
import { formatUtcTimestampLocal, toFriendlyName } from '../../../utils/formatters';
import { getScheduleDisplay } from '../../../utils/cron';

import Spinner from '../../migratorDashboard/Spinner';
import { RollbackProgressStepper } from '../../ProgressTracker/RollbackProgressStepper';

interface ViewModalContentProps {
  job: Migration;
}

interface DisplayResourceEntry {
  key: string;
  primary: string;
  secondary?: string;
}

export const ViewModalContent: React.FC<ViewModalContentProps> = ({ job }) => {
  const { showToast } = useToast();
  const navigate = useNavigate();
  const { accessToken, userInfo } = useAuth();
  const [downloadingLatest, setDownloadingLatest] = useState(false);
  const [downloadingMigration, setDownloadingMigration] = useState(false);
  // Per-user first-time guidance tracking.
  // hasSeen = read from localStorage on mount (determines this session's initial state).
  // showBanner = local session state, starts true if this is their first view.
  const { hasSeen, markAsSeen } = useSnapshotGuidanceSeen(userInfo?.userEmail ?? null);
  const [showBanner, setShowBanner] = useState(!hasSeen);

  // On first view, immediately write to localStorage so next login it won't show again.
  // The banner still stays visible this session until the user clicks X.
  useEffect(() => {
    if (job.isSnapshot && !hasSeen) {
      markAsSeen();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [job.isSnapshot]);

  // Snapshot history is already present on the job when the modal opens —
  // no API call needed.
  const snapshotHistory = useMemo(() => job.snapshotHistory ?? [], [job.snapshotHistory]);

  // Fetch exported resources for this snapshot
  const {
    exportedResources,
    loading: loadingResources,
    error: exportedResourcesError
  } = useFetchExportedResources(accessToken, job.id);

  const scheduleDisplay = getScheduleDisplay(job.snapshotCronExpression);
  const typeLabel = job.isSnapshot ? 'Snapshot' : 'Migration';
  const isRollbackStatus =
    typeof job.status === 'string' && job.status.startsWith('Rollback');

  // Handler for downloading migration results
  const handleDownloadMigration = async () => {
    if (!accessToken) return;
    try {
      setDownloadingMigration(true);
      await downloadMigrationResults(accessToken, job.id);
      showToast('Download started successfully', 'success');
    } catch (error) {
      if (error instanceof NoResultsAvailableError) {
        showToast('No results are available for this migration', 'warning');
      } else {
        showToast('Failed to download migration results', 'error');
      }
    } finally {
      setDownloadingMigration(false);
    }
  };

  const showMigrationDownloadButton = !job.isSnapshot && job.status.toString() === 'completed';

  // Get latest successful snapshot for download button
  const latestSuccessfulSnapshot = useMemo(() => {
    if (!snapshotHistory || snapshotHistory.length === 0) return null;

    // Sort by date descending and find first completed snapshot
    const sorted = [...snapshotHistory].sort(
      (a, b) => new Date(b.capturedAtUtc).getTime() - new Date(a.capturedAtUtc).getTime()
    );
    return sorted.find((entry) => entry.status?.toLowerCase() === 'complete');
  }, [snapshotHistory]);

  // Handler for downloading the latest snapshot
  const handleDownloadLatest = async () => {
    if (!latestSuccessfulSnapshot || !accessToken) return;

    try {
      setDownloadingLatest(true);

      // The timestamp is always the last segment of the blobPath (source of truth)
      const timestamp = latestSuccessfulSnapshot.blobPath.split('/').pop() ?? '';

      await downloadHistoricalSnapshot(accessToken, job.id, timestamp);
      showToast('Snapshot downloaded successfully', 'success');
    } catch (error) {
      console.error('Error downloading latest snapshot:', error);
      showToast('Failed to download snapshot', 'error');
    } finally {
      setDownloadingLatest(false);
    }
  };

  // Process exported resources for display
  const resourcesByType = useMemo(() => {
    if (!exportedResources?.resourceTypes) return new Map<string, DisplayResourceEntry[]>();

    const map = new Map<string, DisplayResourceEntry[]>();

    const getFallbackEntries = (names: string[]): DisplayResourceEntry[] =>
      names.map((name, idx) => ({
        key: `${name}-${idx}`,
        primary: name
      }));

    Object.entries(exportedResources.resourceTypes).forEach(([type, names]) => {
      const detailsForType = exportedResources.resourceDetails?.[type];

      if (Array.isArray(detailsForType) && detailsForType.length > 0) {
        const detailEntries = detailsForType.map((detail: ExportedResourceDetail, idx: number) => {
          if (type === 'genesyscloud_group_roles') {
            const groupName = detail.groupName || detail.key || detail.displayName;
            const roleNames = detail.roleNames || [];
            return {
              key: `${detail.key || groupName}-${idx}`,
              primary: groupName,
              secondary:
                roleNames.length > 0
                  ? `Roles: ${roleNames.join(', ')}`
                  : 'Roles: none assigned'
            };
          }

          return {
            key: `${detail.key || detail.displayName}-${idx}`,
            primary: detail.displayName || detail.key
          };
        });

        map.set(type, detailEntries);
        return;
      }

      map.set(type, getFallbackEntries(names));
    });
    return map;
  }, [exportedResources]);

  const showDownloadButton =
    job.isSnapshot &&
    latestSuccessfulSnapshot &&
    (job.status.toString() === 'SnapshotCompleted' || job.status.toString() === 'SnapshotComplete');

  // --- Status Progress Tracker ---
  type StepStatus = 'completed' | 'current' | 'failed' | 'approval' | 'pending';

  interface ProgressStep {
    label: string;
    subLabel?: string | undefined;
    description: string;
    status: StepStatus;
  }

  const getMigrationSteps = (): ProgressStep[] => {
    const incomingStatus = job.status.toString();

    const exportSubLabel =
      incomingStatus === 'ExportQueued'
        ? 'Queued'
        : incomingStatus === 'ExportInProgress'
          ? 'In Progress'
          : undefined;

    const exportStatus: StepStatus =
      incomingStatus === 'ExportFailed'
        ? 'failed'
        : ['ExportQueued', 'ExportInProgress'].includes(incomingStatus)
          ? 'current'
          : incomingStatus === 'ExportComplete'
            ? 'approval'
            : [
              'TransformationQueued',
              'TransformationInProgress',
              'TransformationComplete',
              'TransformationFailed',
              'ImportQueued',
              'ImportInProgress',
              'ImportFailed',
              'completed',
              'PartiallyCompleted',
              'failed'
            ].includes(incomingStatus)
              ? 'completed'
              : 'pending';

    const transformSubLabel =
      incomingStatus === 'TransformationQueued'
        ? 'Queued'
        : incomingStatus === 'TransformationInProgress'
          ? 'In Progress'
          : undefined;

    const transformStatus: StepStatus =
      incomingStatus === 'TransformationFailed'
        ? 'failed'
        : ['TransformationQueued', 'TransformationInProgress'].includes(incomingStatus)
          ? 'current'
          : incomingStatus === 'TransformationComplete'
            ? 'approval'
            : [
              'ImportQueued',
              'ImportInProgress',
              'ImportFailed',
              'completed',
              'PartiallyCompleted'
            ].includes(incomingStatus)
              ? 'completed'
              : 'pending';

    const importSubLabel =
      incomingStatus === 'ImportQueued'
        ? 'Queued'
        : incomingStatus === 'ImportInProgress'
          ? 'In Progress'
          : undefined;

    const importStatus: StepStatus =
      incomingStatus === 'ImportFailed'
        ? 'failed'
        : ['ImportQueued', 'ImportInProgress'].includes(incomingStatus)
          ? 'current'
          : ['completed', 'PartiallyCompleted'].includes(incomingStatus)
            ? 'completed'
            : 'pending';

    const completeStatus: StepStatus =
      incomingStatus === 'PartiallyCompleted'
        ? 'current'
        : incomingStatus === 'completed'
          ? 'completed'
          : 'pending';

    return [
      {
        label: 'Export',
        subLabel: exportSubLabel,
        description:
          exportStatus === 'current'
            ? 'Exporting resources from the source organization'
            : exportStatus === 'approval'
              ? 'Export complete — awaiting your approval to proceed'
              : exportStatus === 'completed'
                ? 'Resources exported successfully'
                : exportStatus === 'failed'
                  ? 'Export encountered errors'
                  : 'Resources will be exported from the source organization',
        status: exportStatus
      },
      {
        label: 'Transform',
        subLabel: transformSubLabel,
        description:
          transformStatus === 'current'
            ? 'Applying transformations to exported resources'
            : transformStatus === 'approval'
              ? 'Transformation complete — awaiting your approval to proceed'
              : transformStatus === 'completed'
                ? 'Transformations applied successfully'
                : transformStatus === 'failed'
                  ? 'Transformation encountered errors'
                  : 'Transformations will be applied to exported resources',
        status: transformStatus
      },
      {
        label: 'Import',
        subLabel: importSubLabel,
        description:
          importStatus === 'current'
            ? 'Importing resources into the target organization'
            : importStatus === 'completed'
              ? 'Resources imported successfully'
              : importStatus === 'failed'
                ? 'Import encountered errors'
                : 'Resources will be imported into the target organization',
        status: importStatus
      },
      {
        label: 'Complete',
        subLabel: completeStatus === 'current' ? 'Partial' : undefined,
        description:
          completeStatus === 'completed'
            ? 'Migration completed successfully'
            : completeStatus === 'current'
              ? 'Migration partially completed'
              : 'Migration will be marked complete',
        status: completeStatus
      }
    ];
  };

  const getSnapshotSteps = (): ProgressStep[] => {
    const incomingStatus = job.status.toString();

    const exportSubLabel =
      incomingStatus === 'ExportQueued'
        ? 'Queued'
        : incomingStatus === 'ExportInProgress'
          ? 'In Progress'
          : undefined;

    const snapshotExportStatus: StepStatus =
      incomingStatus === 'ExportFailed'
        ? 'failed'
        : ['ExportQueued', 'ExportInProgress'].includes(incomingStatus)
          ? 'current'
          : incomingStatus === 'SnapshotCompleted'
            ? 'completed'
            : 'pending';

    const completeStatus: StepStatus =
      incomingStatus === 'SnapshotCompleted' ? 'completed' : 'pending';

    return [
      {
        label: 'Export',
        subLabel: exportSubLabel,
        description:
          snapshotExportStatus === 'current'
            ? 'Snapshot is currently capturing resources'
            : snapshotExportStatus === 'failed'
              ? 'Snapshot encountered errors'
              : snapshotExportStatus === 'completed'
                ? 'Resources captured from the source organization'
                : 'Resources will be captured from the source organization',
        status: snapshotExportStatus
      },
      {
        label: 'Complete',
        description:
          completeStatus === 'completed'
            ? 'Snapshot completed successfully'
            : 'Snapshot will be marked complete once all resources are captured',
        status: completeStatus
      }
    ];
  };

  const progressSteps = job.isSnapshot ? getSnapshotSteps() : getMigrationSteps();

  const stepBg: Record<StepStatus, string> = {
    completed: 'bg-green-500 dark:bg-green-600',
    current: 'bg-blue-500 dark:bg-blue-600',
    failed: 'bg-red-500 dark:bg-red-600',
    approval: 'bg-orange-500 dark:bg-orange-600',
    pending: 'bg-gray-300 dark:bg-gray-600'
  };

  const stepIcon = (status: StepStatus, index: number) => {
    if (status === 'completed')
      return (
        <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 20 20">
          <path
            fillRule="evenodd"
            d="M16.707 5.293a1 1 0 00-1.414 0L8 12.586 4.707 9.293a1 1 0 00-1.414 1.414l4 4a1 1 0 001.414 0l8-8a1 1 0 000-1.414z"
            clipRule="evenodd"
          />
        </svg>
      );
    if (status === 'failed')
      return (
        <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 20 20">
          <path
            fillRule="evenodd"
            d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
            clipRule="evenodd"
          />
        </svg>
      );
    if (status === 'approval')
      return (
        <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 20 20">
          <path
            fillRule="evenodd"
            d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z"
            clipRule="evenodd"
          />
        </svg>
      );
    if (status === 'current')
      return <span className="w-2 h-2 rounded-full bg-white animate-pulse block" />;
    return <span className="text-xs font-bold text-gray-500 dark:text-gray-400">{index + 1}</span>;
  };

  return (
    <div className="space-y-4">
      {/* Snapshot guidance banner — first-time view only.
          markAsSeen() is called on mount so next login hasSeen=true (tooltip shown).
          X only hides the banner for this session via showBanner. */}
      {job.isSnapshot && showBanner && (
        <div className="rounded-xl border border-amber-200 dark:border-amber-700 bg-amber-50 dark:bg-amber-900/20 p-4">
          <div className={`flex items-start justify-between ${ui.gap.md}`}>
            <div className={`flex items-start ${ui.gap.md}`}>
              {/* Info icon */}
              <div className="flex-shrink-0 mt-0.5 w-5 h-5 rounded-full bg-amber-400 dark:bg-amber-600 flex items-center justify-center">
                <svg
                  className="w-3 h-3 text-white"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                  strokeWidth={3}
                >
                  <path strokeLinecap="round" strokeLinejoin="round" d="M12 6v.01M12 12v6" />
                </svg>
              </div>
              <div className={`${ui.text.label} text-amber-800 dark:text-amber-200 space-y-2`}>
                <p className={`${ui.text.body} font-semibold text-amber-900 dark:text-amber-100`}>
                  About snapshots
                </p>
                <div className="space-y-1.5">
                  <div className="flex items-start gap-2">
                    <span className="mt-1 w-1.5 h-1.5 rounded-full bg-amber-400 dark:bg-amber-500 flex-shrink-0" />
                    <div>
                      <span className="font-semibold text-amber-900 dark:text-amber-100">
                        What a snapshot is —{' '}
                      </span>
                      A point-in-time capture of your Genesys Cloud objects and resources.
                    </div>
                  </div>
                  <div className="flex items-start gap-2">
                    <span className="mt-1 w-1.5 h-1.5 rounded-full bg-amber-400 dark:bg-amber-500 flex-shrink-0" />
                    <div>
                      <span className="font-semibold text-amber-900 dark:text-amber-100">
                        Where snapshot files are stored —{' '}
                      </span>
                      In RepliCloud, accessible from this dashboard. Download them at any time.
                    </div>
                  </div>
                  <div className="flex items-start gap-2">
                    <span className="mt-1 w-1.5 h-1.5 rounded-full bg-amber-400 dark:bg-amber-500 flex-shrink-0" />
                    <div>
                      <span className="font-semibold text-amber-900 dark:text-amber-100">
                        Who uses snapshots (admins/developers) —{' '}
                      </span>
                      Genesys Cloud administrators and developers use snapshots to review
                      environment resources, check dependencies, and reference settings for manual
                      restore.
                    </div>
                  </div>
                </div>
              </div>
            </div>
            {/* Dismiss button */}
            <button
              type="button"
              onClick={() => setShowBanner(false)}
              title="Dismiss"
              className="flex-shrink-0 text-amber-500 dark:text-amber-400 hover:text-amber-700 dark:hover:text-amber-200 transition-colors"
              aria-label="Dismiss snapshot guidance"
            >
              <svg
                className={ui.svg.sm}
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                strokeWidth={2}
              >
                <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>
      )}

      {/* Hero Section - Name & Status */}
      <div
        className={`bg-gradient-to-r from-gray-50 to-white dark:from-gray-800 dark:to-gray-900 rounded-xl border border-gray-200 dark:border-gray-700 ${ui.page.cardPadding}`}
      >
        <div className={`flex items-start justify-between ${ui.gap.lg}`}>
          <div className="flex-1">
            <div
              className={`flex items-center ${ui.gap.xs} ${ui.text.label} font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider ${ui.mb.sm}`}
            >
              {typeLabel} Name
            </div>
            <div
              className={`${ui.text.sectionTitle} font-bold text-gray-900 dark:text-gray-100 break-words`}
            >
              {job.name}
            </div>
          </div>
          <div
            className={`flex items-center gap-1 ${ui.input.modal} bg-green-100 dark:bg-green-900/30 rounded-lg border border-green-200 dark:border-green-700 flex-shrink-0`}
          >
            <svg
              className={`${ui.svg.sm} text-green-600 dark:text-green-400`}
              fill="currentColor"
              viewBox="0 0 20 20"
            >
              <path
                fillRule="evenodd"
                d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                clipRule="evenodd"
              />
            </svg>
            <span className={`${ui.text.badge} font-semibold text-green-700 dark:text-green-300`}>
              {getStatusLabel(job.status.toString())}
            </span>
          </div>
        </div>
      </div>

      {/* Download Migration Results Button */}
      {showMigrationDownloadButton && (
        <div className="relative group/download">
          <button
            onClick={handleDownloadMigration}
            disabled={downloadingMigration}
            className={`w-full flex items-center justify-center ${ui.gap.md} ${ui.button.lg} bg-purple-600 hover:bg-purple-700 dark:bg-purple-700 dark:hover:bg-purple-800 text-white rounded-xl transition-colors disabled:opacity-50 disabled:cursor-not-allowed font-semibold shadow-lg hover:shadow-xl`}
          >
            {downloadingMigration ? (
              <>
                <svg className={`${ui.svg.md} animate-spin`} fill="none" viewBox="0 0 24 24">
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                  />
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  />
                </svg>
                <span className={`${ui.text.body}`}>Downloading Migration Results...</span>
              </>
            ) : (
              <>
                <svg className={`${ui.svg.md}`} fill="currentColor" viewBox="0 0 20 20">
                  <path
                    fillRule="evenodd"
                    d="M3 17a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm3.293-7.707a1 1 0 011.414 0L9 10.586V3a1 1 0 112 0v7.586l1.293-1.293a1 1 0 111.414 1.414l-3 3a1 1 0 01-1.414 0l-3-3a1 1 0 010-1.414z"
                    clipRule="evenodd"
                  />
                </svg>
                <span className={`${ui.text.body}`}>Download Migration Results (Optional)</span>
              </>
            )}
          </button>
          <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 hidden group-hover/download:block z-50 pointer-events-none w-64">
            <div className="bg-gray-900 dark:bg-gray-700 text-white text-xs rounded-lg px-3 py-2 shadow-lg text-center leading-relaxed">
              Optional: download migration results for offline review. This does not affect your
              migration status.
            </div>
          </div>
        </div>
      )}

      {/* View Change Details Button — migration only, completed status */}
      {!job.isSnapshot && job.status.toString() === 'completed' && (
        <button
          type="button"
          onClick={() => navigate('/SnapshotComparisonTool', { state: { job, modalVariant: 'view' } })}
          className={`w-full flex items-center justify-center ${ui.gap.md} ${ui.button.lg} bg-indigo-600 hover:bg-indigo-700 dark:bg-indigo-500 dark:hover:bg-indigo-600 text-white rounded-xl transition-colors font-semibold shadow-lg hover:shadow-xl`}
        >
          <svg
            className={ui.svg.md}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
            strokeWidth={2}
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"
            />
          </svg>
          <span className={ui.text.body}>Review Target Organization Changes</span>
        </button>
      )}

      {/* Download Latest Snapshot Button */}
      {showDownloadButton && (
        <button
          onClick={handleDownloadLatest}
          disabled={downloadingLatest}
          className={`w-full flex items-center justify-center ${ui.gap.md} ${ui.button.lg} bg-blue-600 hover:bg-blue-700 dark:bg-blue-700 dark:hover:bg-blue-600 text-white rounded-xl transition-colors disabled:opacity-50 disabled:cursor-not-allowed font-semibold shadow-lg hover:shadow-xl`}
        >
          {downloadingLatest ? (
            <>
              <svg className={`${ui.svg.md} animate-spin`} fill="none" viewBox="0 0 24 24">
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                />
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                />
              </svg>
              <span className={`${ui.text.body}`}>Downloading Latest Snapshot...</span>
            </>
          ) : (
            <>
              <svg className={`${ui.svg.md}`} fill="currentColor" viewBox="0 0 20 20">
                <path
                  fillRule="evenodd"
                  d="M3 17a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm3.293-7.707a1 1 0 011.414 0L9 10.586V3a1 1 0 112 0v7.586l1.293-1.293a1 1 0 111.414 1.414l-3 3a1 1 0 01-1.414 0l-3-3a1 1 0 010-1.414z"
                  clipRule="evenodd"
                />
              </svg>
              <span className={`${ui.text.body}`}>Download Latest Snapshot</span>
            </>
          )}
        </button>
      )}

      {/* Schedule Display */}
      {scheduleDisplay && (
        <div
          className={`flex items-center ${ui.gap.lg} ${ui.page.sectionPadding} bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20 rounded-lg border border-blue-200 dark:border-blue-700`}
        >
          <div className="p-2 bg-blue-100 dark:bg-blue-800/50 rounded-lg">
            <svg
              className={`${ui.svg.sm} text-blue-600 dark:text-blue-400`}
              fill="currentColor"
              viewBox="0 0 20 20"
            >
              <path
                fillRule="evenodd"
                d="M4 2a1 1 0 011 1v2.101a7.002 7.002 0 0111.601 2.566 1 1 0 11-1.885.666A5.002 5.002 0 005.999 7H9a1 1 0 010 2H4a1 1 0 01-1-1V3a1 1 0 011-1zm.008 9.057a1 1 0 011.276.61A5.002 5.002 0 0014.001 13H11a1 1 0 110-2h5a1 1 0 011 1v5a1 1 0 11-2 0v-2.101a7.002 7.002 0 01-11.601-2.566 1 1 0 01.61-1.276z"
                clipRule="evenodd"
              />
            </svg>
          </div>
          <div>
            <div className={`${ui.text.label} font-medium text-blue-700 dark:text-blue-300 mb-1`}>
              Recurring Schedule
            </div>
            <div className={`${ui.text.label} font-bold text-blue-900 dark:text-blue-100`}>
              {scheduleDisplay}
            </div>
          </div>
        </div>
      )}

      {/* Status Progress Tracker */}
      {isRollbackStatus ? (
        <RollbackProgressStepper status={job.status.toString()} />
      ) : (
        <div className="p-4 bg-gray-50 dark:bg-gray-800/50 rounded-lg border border-gray-200 dark:border-gray-700">
          <div
            className={`${ui.text.label} font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-3`}
          >
            {typeLabel} Progress
          </div>
          <div className="relative flex items-start gap-0">
            {progressSteps.map((step, index) => (
              <div key={step.label} className="flex-1 flex flex-col items-center relative">
                {/* Connector line */}
                {index < progressSteps.length - 1 && (
                  <div
                    className={`absolute top-3.5 left-1/2 w-full h-0.5 z-0 ${step.status === 'completed'
                      ? 'bg-green-400 dark:bg-green-600'
                      : 'bg-gray-300 dark:bg-gray-600'
                      }`}
                  />
                )}

                {/* Circle */}
                <div
                  className={`relative z-10 w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 ${stepBg[step.status]} ${step.status === 'current'
                    ? 'ring-2 ring-offset-2 ring-blue-400 dark:ring-offset-gray-800'
                    : ''
                    } ${step.status === 'approval'
                      ? 'ring-2 ring-offset-2 ring-orange-300 dark:ring-offset-gray-800'
                      : ''
                    }`}
                >
                  {stepIcon(step.status, index)}
                </div>

                {/* Label + description */}
                <div className="mt-2 text-center px-1">
                  <div className="flex items-center justify-center gap-1 flex-wrap">
                    <div
                      className={`font-semibold ${ui.text.label} ${step.status === 'completed'
                        ? 'text-green-700 dark:text-green-400'
                        : step.status === 'current'
                          ? 'text-blue-700 dark:text-blue-300'
                          : step.status === 'failed'
                            ? 'text-red-600 dark:text-red-400'
                            : step.status === 'approval'
                              ? 'text-orange-600 dark:text-orange-400'
                              : 'text-gray-400 dark:text-gray-500'
                        }`}
                    >
                      {step.label}
                    </div>
                    {step.subLabel && (
                      <span
                        className={`inline-block ${ui.text.label} font-semibold px-1.5 py-0.5 rounded-full leading-none ${step.subLabel === 'Queued'
                          ? 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/40 dark:text-yellow-300'
                          : step.subLabel === 'Partial'
                            ? 'bg-orange-100 text-orange-700 dark:bg-orange-900/40 dark:text-orange-300'
                            : 'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300'
                          }`}
                      >
                        {step.subLabel}
                      </span>
                    )}
                  </div>
                  <div
                    className={`${ui.text.label} text-gray-500 dark:text-gray-400 mt-0.5 leading-tight`}
                  >
                    {step.description}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Selected Resources Section */}
      {(job.isSnapshot || !job.isSnapshot) && (
        <div className="p-4 bg-gray-50 dark:bg-gray-800/50 rounded-lg border border-gray-200 dark:border-gray-700">
          <div
            className={`${ui.text.label} font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-3 flex items-center`}
          >
            Selected Resources
            <Tooltip
              position="right"
              content={
                job.isSnapshot
                  ? 'The resources that were selected when this snapshot was configured. This shows what will be captured when the snapshot runs.'
                  : 'The resources that were selected when this migration was configured.'
              }
            />
          </div>

          {loadingResources ? (
            <div className="flex flex-col items-center gap-2 py-6">
              <Spinner />
              <p className={`${ui.text.body} text-gray-500 dark:text-gray-400`}>
                Loading resources...
              </p>
            </div>
          ) : exportedResourcesError ? (
            <div className="py-6 text-center">
              <p className={`${ui.text.body} text-gray-500 dark:text-gray-400`}>
                Failed to load resources
              </p>
              <p className={`${ui.text.xs} text-gray-400 dark:text-gray-500 mt-1`}>
                {exportedResourcesError.message}
              </p>
            </div>
          ) : job.exportAll ? (
            <div
              className={`flex items-center ${ui.gap.md} py-4 px-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-700`}
            >
              <svg
                className={`${ui.svg.md} text-blue-600 dark:text-blue-400 flex-shrink-0`}
                fill="currentColor"
                viewBox="0 0 20 20"
              >
                <path
                  fillRule="evenodd"
                  d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                  clipRule="evenodd"
                />
              </svg>
              <div>
                <div className={`${ui.text.body} font-semibold text-blue-900 dark:text-blue-100`}>
                  Full Snapshot
                </div>
                <div className={`${ui.text.xs} text-blue-700 dark:text-blue-300 mt-0.5`}>
                  All supported resource types are included in this snapshot
                </div>
              </div>
            </div>
          ) : resourcesByType.size > 0 ? (
            <div className="divide-y divide-gray-200 dark:divide-gray-700 border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden max-h-80 overflow-y-auto">
              {Array.from(resourcesByType.entries()).map(([type, entries]) => (
                <details key={type} className="group bg-white dark:bg-gray-800">
                  <summary
                    className={`flex items-center justify-between ${ui.page.rowPadding} ${ui.gap.md} cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-900 transition`}
                  >
                    <div className="flex items-center gap-3">
                      <svg
                        className={`${ui.svg.sm} text-gray-400 transition-transform group-open:rotate-90`}
                        fill="currentColor"
                        viewBox="0 0 20 20"
                      >
                        <path
                          fillRule="evenodd"
                          d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z"
                          clipRule="evenodd"
                        />
                      </svg>
                      <span
                        className={`${ui.text.body} font-semibold text-gray-900 dark:text-gray-100`}
                      >
                        {getTypeLabelByApiName(toFriendlyName(type))}
                      </span>
                    </div>
                    <span
                      className={`${ui.text.xs} font-bold text-white bg-blue-600 dark:bg-blue-700 ${ui.badge.padding} rounded-full whitespace-nowrap`}
                    >
                      {entries.length}
                    </span>
                  </summary>

                  <div
                    className={`${ui.page.rowPadding} bg-gray-50 dark:bg-gray-900 max-h-48 overflow-y-auto`}
                  >
                    <div className="space-y-1">
                      {entries.map((entry) => (
                        <div
                          key={entry.key}
                          className={`flex items-start gap-2 py-1 min-w-0 ${ui.text.label} text-gray-700 dark:text-gray-300`}
                        >
                          <svg
                            className={`${ui.svg.sm} text-green-500 flex-shrink-0 mt-0.5`}
                            fill="currentColor"
                            viewBox="0 0 20 20"
                          >
                            <path
                              fillRule="evenodd"
                              d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                              clipRule="evenodd"
                            />
                          </svg>
                          <div className="min-w-0">
                            <div className="break-all">{entry.primary}</div>
                            {entry.secondary && (
                              <div className={`${ui.text.xs} text-gray-500 dark:text-gray-400 mt-0.5 break-all`}>
                                {entry.secondary}
                              </div>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </details>
              ))}
            </div>
          ) : (
            <div className="py-6 text-center text-gray-400 dark:text-gray-500 italic">
              <p className={`${ui.text.body}`}>No resources selected</p>
            </div>
          )}
        </div>
      )}

      {/* Details Grid */}
      <div className="grid grid-cols-2 gap-4">
        <div className="flex items-start gap-3 p-4 bg-gray-50 dark:bg-gray-800/50 rounded-lg border border-gray-200 dark:border-gray-700">
          <div className="p-2 bg-blue-100 dark:bg-blue-900/30 rounded-lg">
            <svg
              className={`${ui.svg.sm} text-blue-600 dark:text-blue-400`}
              fill="currentColor"
              viewBox="0 0 20 20"
            >
              <path d="M9 2a1 1 0 000 2h2a1 1 0 100-2H9z" />
              <path
                fillRule="evenodd"
                d="M4 5a2 2 0 012-2 3 3 0 003 3h2a3 3 0 003-3 2 2 0 012 2v11a2 2 0 01-2 2H6a2 2 0 01-2-2V5zm3 4a1 1 0 000 2h.01a1 1 0 100-2H7zm3 0a1 1 0 000 2h3a1 1 0 100-2h-3zm-3 4a1 1 0 100 2h.01a1 1 0 100-2H7zm3 0a1 1 0 100 2h3a1 1 0 100-2h-3z"
                clipRule="evenodd"
              />
            </svg>
          </div>
          <div className="flex-1 min-w-0">
            <div className={`${ui.text.label} font-medium text-gray-500 dark:text-gray-400 mb-1`}>
              Type
            </div>
            <div className={`font-semibold ${ui.text.label} text-gray-900 dark:text-gray-100`}>
              {typeLabel}
            </div>
          </div>
        </div>

        <div className="flex items-start gap-3 p-4 bg-gray-50 dark:bg-gray-800/50 rounded-lg border border-gray-200 dark:border-gray-700">
          <div className="p-2 bg-purple-100 dark:bg-purple-900/30 rounded-lg">
            <svg
              className={`${ui.svg.sm} text-purple-600 dark:text-purple-400`}
              fill="currentColor"
              viewBox="0 0 20 20"
            >
              <path
                fillRule="evenodd"
                d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z"
                clipRule="evenodd"
              />
            </svg>
          </div>
          <div className="flex-1 min-w-0">
            <div className={`${ui.text.label} font-medium text-gray-500 dark:text-gray-400 mb-1`}>
              Created By
            </div>
            <div
              className={`font-semibold ${ui.text.label} text-gray-900 dark:text-gray-100 md:truncate xl:whitespace-normal xl:overflow-visible`}
              title={job.createdBy}
            >
              {job.createdBy}
            </div>
          </div>
        </div>

        <div className="flex items-start gap-3 p-4 bg-gray-50 dark:bg-gray-800/50 rounded-lg border border-gray-200 dark:border-gray-700">
          <div className="p-2 bg-orange-100 dark:bg-orange-900/30 rounded-lg">
            <svg
              className={`${ui.svg.sm} text-orange-600 dark:text-orange-400`}
              fill="currentColor"
              viewBox="0 0 20 20"
            >
              <path
                fillRule="evenodd"
                d="M6 2a1 1 0 00-1 1v1H4a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V6a2 2 0 00-2-2h-1V3a1 1 0 10-2 0v1H7V3a1 1 0 00-1-1zm0 5a1 1 0 000 2h8a1 1 0 100-2H6z"
                clipRule="evenodd"
              />
            </svg>
          </div>
          <div className="flex-1 min-w-0">
            <div className={`${ui.text.label} font-medium text-gray-500 dark:text-gray-400 mb-1`}>
              Created At
            </div>
            <div className={`font-semibold ${ui.text.label} text-gray-900 dark:text-gray-100`}>
              {formatUtcTimestampLocal(job.createdAtUtc)}
            </div>
          </div>
        </div>

        {job.updatedAtUtc && (
          <div className="flex items-start gap-3 p-4 bg-gray-50 dark:bg-gray-800/50 rounded-lg border border-gray-200 dark:border-gray-700">
            <div className="p-2 bg-teal-100 dark:bg-teal-900/30 rounded-lg">
              <svg
                className={`${ui.svg.sm} text-teal-600 dark:text-teal-400`}
                fill="currentColor"
                viewBox="0 0 20 20"
              >
                <path
                  fillRule="evenodd"
                  d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z"
                  clipRule="evenodd"
                />
              </svg>
            </div>
            <div className="flex-1 min-w-0">
              <div className={`${ui.text.label} font-medium text-gray-500 dark:text-gray-400 mb-1`}>
                Last Updated
              </div>
              <div className={`font-semibold ${ui.text.label} text-gray-900 dark:text-gray-100`}>
                {formatUtcTimestampLocal(job.updatedAtUtc)}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Snapshot History Section */}
      {job.isSnapshot && (
        <div className="mt-6 pt-6 border-t border-gray-200 dark:border-gray-700">
          <h3
            className={`${ui.text.subTitle} font-semibold text-gray-900 dark:text-gray-100 ${ui.mb.title} flex items-center`}
          >
            Snapshot History
            <Tooltip
              position="right"
              content="A log of all snapshots captured for this migration. Each row represents one scheduled or manual export data. Click Download on any row to download that snapshot's exported results as a ZIP file."
            />
          </h3>
          {snapshotHistory.length > 0 ? (
            <SnapshotHistoryTable history={snapshotHistory} migrationId={job.id} />
          ) : (
            <div
              className={`flex items-center justify-center py-6 ${ui.text.label} text-gray-400 dark:text-gray-500 italic`}
            >
              None
            </div>
          )}
        </div>
      )}
    </div>
  );
};
